import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Play, Award, Volume2, VolumeX } from "lucide-react";
import { LOGO_URL } from "../lib/constants";
import {
    trackViewContent,
    trackCompleteRegistration,
} from "../lib/pixel";

/**
 * Sleep LP — revised structure.
 *
 * 1. Logo
 * 2. Hero — "Your Free 8-Hour Sleep Track is Waiting"
 * 3. "Just Imagine" hook (Sleep · Anxiety · Stress in one block)
 * 4. Tatva K creds line (extended)
 * 5. Featured Teacher on Insight Timer badge
 * 6. Landscape 16:9 video (2-3 min) — drop /brochure-assets/sleep-lp-video.mp4
 * 7. Primary CTA — Follow & Rate on Insight Timer
 * 8. Sub-line — Follow · Listen · Rate
 * 9. Trust strip — 150M+ · 182K · 4,400+
 * 10. 3 Pillars with "Just Imagine" micro-copy
 *
 * Compliance: "therapy" only inside Berklee credential.
 */

const IT_PROFILE_URL = "https://www.insighttimer.com/resetsanctuary";
const VIDEO_SRC = "/brochure-assets/sleep-lp-video.mp4";
const VIDEO_POSTER = "/brochure-assets/sleep-lp-poster.jpg";
const GOLD = "#c9a227";

const Sleep = () => {
    const videoRef = useRef(null);
    const [muted, setMuted] = useState(true);

    // Fire Meta Pixel ViewContent — signals high-intent funnel entry to Andromeda
    useEffect(() => {
        trackViewContent({
            content_name: "Sleep Funnel — 8 Hour Track",
            content_category: "sleep_lp",
        });
    }, []);

    const handleITClick = () => {
        // Fire CompleteRegistration when user follows on Insight Timer
        trackCompleteRegistration({
            content_name: "Insight Timer Follow — Sleep Funnel",
        });
    };

    const toggleSound = () => {
        const v = videoRef.current;
        if (!v) return;
        const next = !muted;
        v.muted = next;
        setMuted(next);
        if (!next) {
            // user opted in to sound — make sure it's playing
            v.play().catch(() => {});
        }
    };

    return (
        <main
            data-testid="sleep-page"
            style={{
                backgroundColor: "#0a0a0a",
                minHeight: "100vh",
                color: "#bbb",
                fontFamily: "Inter, sans-serif",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "40px 20px 80px",
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* Ambient gold glow */}
            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    top: "-200px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 900,
                    height: 900,
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, rgba(201,162,39,0.10) 0%, rgba(201,162,39,0) 60%)",
                    pointerEvents: "none",
                    zIndex: 0,
                }}
            />

            {/* Logo */}
            <div
                style={{
                    position: "relative",
                    zIndex: 1,
                    marginBottom: 32,
                }}
            >
                <img
                    src={LOGO_URL}
                    alt="The Reset Sanctuary"
                    style={{ height: 64 }}
                />
            </div>

            {/* HERO */}
            <section
                data-testid="sleep-hero"
                style={{
                    position: "relative",
                    zIndex: 1,
                    width: "100%",
                    maxWidth: 920,
                    textAlign: "center",
                    marginBottom: 28,
                }}
            >
                <p
                    style={{
                        color: GOLD,
                        fontSize: 10,
                        letterSpacing: "0.4em",
                        textTransform: "uppercase",
                        margin: "0 0 16px",
                        fontWeight: 500,
                    }}
                    data-testid="sleep-kicker"
                >
                    Free · 8 Hours · By Tatva K
                </p>
                <h1
                    className="font-serif"
                    style={{
                        color: "#fff",
                        fontSize: "clamp(36px, 6vw, 60px)",
                        fontWeight: 400,
                        lineHeight: 1.04,
                        letterSpacing: "-0.025em",
                        margin: "0 0 14px",
                    }}
                    data-testid="sleep-headline"
                >
                    Your Free 8-Hour Sleep Track is{" "}
                    <span style={{ color: GOLD }}>Waiting.</span>
                </h1>
            </section>

            {/* JUST IMAGINE HOOK */}
            <section
                data-testid="sleep-imagine"
                style={{
                    position: "relative",
                    zIndex: 1,
                    width: "100%",
                    maxWidth: 760,
                    textAlign: "center",
                    marginBottom: 36,
                    padding: "0 8px",
                }}
            >
                <p
                    style={{
                        color: GOLD,
                        fontSize: 10,
                        letterSpacing: "0.4em",
                        textTransform: "uppercase",
                        margin: "0 0 14px",
                        fontWeight: 500,
                    }}
                >
                    Just Imagine
                </p>
                <p
                    className="font-serif"
                    style={{
                        color: "#fff",
                        fontSize: "clamp(20px, 2.4vw, 26px)",
                        lineHeight: 1.4,
                        margin: 0,
                        fontWeight: 400,
                        letterSpacing: "-0.01em",
                    }}
                >
                    Waking up restored — not foggy.{" "}
                    <span style={{ color: GOLD }}>·</span> Your mind quiet —
                    not racing.{" "}
                    <span style={{ color: GOLD }}>·</span> Your body calm —
                    not tense.
                </p>
            </section>

            {/* CREDS LINE — extended */}
            <p
                data-testid="sleep-creds"
                style={{
                    position: "relative",
                    zIndex: 1,
                    color: "#888",
                    fontSize: 10,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    margin: "0 0 22px",
                    textAlign: "center",
                    maxWidth: 920,
                    lineHeight: 1.8,
                    padding: "0 12px",
                    fontWeight: 400,
                }}
            >
                TEDx Speaker · 150M+ Streams · 182K Monthly Listeners · Reiki
                Grandmaster · ISTA &amp; IPHM Certified · Berklee College of
                Music — Music Therapy Masterclass · Featured by BBC Radio 1,
                BBC Asian Network &amp; Rolling Stone India · Curated alongside
                Talvin Singh and DJ Spooky
            </p>

            {/* FEATURED TEACHER BADGE */}
            <div
                data-testid="sleep-featured-badge"
                style={{
                    position: "relative",
                    zIndex: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "12px 22px",
                    border: `1px solid ${GOLD}`,
                    backgroundColor: "rgba(201,162,39,0.06)",
                    marginBottom: 32,
                    maxWidth: 720,
                }}
            >
                <Award size={20} color={GOLD} strokeWidth={1.5} />
                <div style={{ textAlign: "left" }}>
                    <p
                        style={{
                            color: GOLD,
                            fontSize: 10,
                            letterSpacing: "0.32em",
                            textTransform: "uppercase",
                            margin: 0,
                            fontWeight: 600,
                            lineHeight: 1.2,
                        }}
                    >
                        Tracks Featured on Insight Timer
                    </p>
                    <p
                        style={{
                            color: "#bbb",
                            fontSize: 9.5,
                            letterSpacing: "0.18em",
                            textTransform: "uppercase",
                            margin: "4px 0 0",
                            fontWeight: 400,
                            lineHeight: 1.3,
                        }}
                    >
                        The World&apos;s Largest Meditation App · Featured in
                        Forbes &amp; The New York Times
                    </p>
                </div>
            </div>

            {/* LANDSCAPE VIDEO 16:9 — autoplay muted loop, tap-for-sound */}
            <section
                data-testid="sleep-video"
                style={{
                    position: "relative",
                    zIndex: 1,
                    width: "100%",
                    maxWidth: 920,
                    marginBottom: 32,
                    border: "1px solid rgba(201,162,39,0.28)",
                    backgroundColor: "#000",
                    boxShadow:
                        "0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(201,162,39,0.06)",
                }}
            >
                <div
                    style={{
                        position: "relative",
                        width: "100%",
                        aspectRatio: "16 / 9",
                        overflow: "hidden",
                    }}
                >
                    <video
                        ref={videoRef}
                        data-testid="sleep-video-player"
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        poster={VIDEO_POSTER}
                        style={{
                            width: "100%",
                            height: "100%",
                            display: "block",
                            backgroundColor: "#000",
                            objectFit: "cover",
                        }}
                    >
                        <source src={VIDEO_SRC} type="video/mp4" />
                    </video>
                    {/* TAP-FOR-SOUND OVERLAY (top-right corner) */}
                    <button
                        type="button"
                        onClick={toggleSound}
                        data-testid="sleep-video-sound-toggle"
                        aria-label={
                            muted ? "Tap for sound" : "Mute video"
                        }
                        style={{
                            position: "absolute",
                            top: 14,
                            right: 14,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "9px 14px",
                            backgroundColor: "rgba(10,10,10,0.7)",
                            backdropFilter: "blur(8px)",
                            WebkitBackdropFilter: "blur(8px)",
                            border: `1px solid ${GOLD}`,
                            color: GOLD,
                            fontSize: 11,
                            letterSpacing: "0.22em",
                            textTransform: "uppercase",
                            fontWeight: 600,
                            fontFamily: "Inter, sans-serif",
                            cursor: "pointer",
                            transition: "background-color 160ms ease",
                        }}
                    >
                        {muted ? (
                            <>
                                <VolumeX size={14} strokeWidth={2} />
                                Tap for Sound
                            </>
                        ) : (
                            <>
                                <Volume2 size={14} strokeWidth={2} />
                                Sound On
                            </>
                        )}
                    </button>
                </div>
            </section>

            {/* PRIMARY CTA — bigger, gold, highlighted */}
            <a
                href={IT_PROFILE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleITClick}
                data-testid="sleep-cta"
                style={{
                    position: "relative",
                    zIndex: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 14,
                    padding: "22px 40px",
                    backgroundColor: GOLD,
                    color: "#0a0a0a",
                    fontSize: "clamp(13px, 1.55vw, 16px)",
                    letterSpacing: "0.26em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    textDecoration: "none",
                    fontFamily: "Inter, sans-serif",
                    boxShadow:
                        "0 18px 60px rgba(201,162,39,0.35), 0 0 0 2px rgba(201,162,39,0.4)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    textAlign: "center",
                    maxWidth: "100%",
                    lineHeight: 1.3,
                }}
            >
                <Play size={16} fill="#0a0a0a" strokeWidth={0} />
                Follow &amp; Rate on Insight Timer — Get Your Free Track
                <ArrowRight size={16} />
            </a>

            {/* Sub-line under CTA */}
            <p
                data-testid="sleep-cta-note"
                style={{
                    position: "relative",
                    zIndex: 1,
                    color: "#888",
                    fontSize: 10.5,
                    letterSpacing: "0.32em",
                    textTransform: "uppercase",
                    margin: "20px 0 0",
                    textAlign: "center",
                    fontWeight: 500,
                    maxWidth: 560,
                    lineHeight: 1.6,
                }}
            >
                Follow Tatva K · Listen Tonight · Drop a Rating
            </p>

            {/* TESTIMONIALS — Mid-session video proof */}
            <section data-testid="sleep-testimonials" style={{ position: "relative", zIndex: 1, marginTop: 64, maxWidth: 1100, marginLeft: "auto", marginRight: "auto", padding: "0 8px" }}>
                <p style={{ color: "#c9a227", fontSize: 11, letterSpacing: "0.34em", textTransform: "uppercase", fontWeight: 600, margin: "0 0 14px", textAlign: "center" }}>
                    Mid-Session · No Words Needed
                </p>
                <h2 className="font-serif" style={{ fontSize: "clamp(24px, 3.5vw, 32px)", fontWeight: 400, margin: "0 0 32px", lineHeight: 1.18, textAlign: "center", color: "#fff" }}>
                    They lay down. <span style={{ color: "#c9a227", fontStyle: "italic" }}>Then they slept.</span>
                </h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
                    {[
                        { src: "/brochure-assets/testimonials/sachin.mp4", poster: "/brochure-assets/testimonials/sachin-poster.jpg", name: "Sachin · Dubai" },
                        { src: "/brochure-assets/testimonials/martyn.mp4", poster: "/brochure-assets/testimonials/martyn-poster.jpg", name: "Martyn · Dubai" },
                        { src: "/brochure-assets/testimonials/daniel.mp4", poster: "/brochure-assets/testimonials/daniel-poster.jpg", name: "Daniel · UK" },
                    ].map((v, i) => (
                        <div key={i} style={{ border: "1px solid rgba(201,162,39,0.25)", backgroundColor: "#0a0a0a" }}>
                            <video controls playsInline preload="metadata" poster={v.poster} style={{ width: "100%", aspectRatio: "9 / 16", objectFit: "cover", display: "block", backgroundColor: "#000" }}>
                                <source src={v.src} type="video/mp4" />
                            </video>
                            <p style={{ color: "#c9a227", fontSize: 10.5, letterSpacing: "0.24em", textTransform: "uppercase", padding: "10px 12px", margin: 0, fontWeight: 600, textAlign: "center" }}>
                                {v.name} · Mid-Session
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* TRUST STRIP */}
            <section
                data-testid="sleep-trust"
                style={{
                    position: "relative",
                    zIndex: 1,
                    marginTop: 56,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "20px 40px",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    maxWidth: 720,
                }}
            >
                {[
                    { num: "150M+", label: "Streams" },
                    { num: "182K", label: "Monthly Listeners" },
                    { num: "4,400+", label: "Reset Hours Delivered" },
                ].map((s) => (
                    <div key={s.label}>
                        <p
                            className="font-serif"
                            style={{
                                color: GOLD,
                                fontSize: 28,
                                margin: 0,
                                fontWeight: 400,
                                lineHeight: 1,
                                letterSpacing: "-0.01em",
                            }}
                        >
                            {s.num}
                        </p>
                        <p
                            style={{
                                color: "#888",
                                fontSize: 9,
                                letterSpacing: "0.3em",
                                textTransform: "uppercase",
                                margin: "8px 0 0",
                                fontWeight: 500,
                            }}
                        >
                            {s.label}
                        </p>
                    </div>
                ))}
            </section>

            {/* CONTENT PILLARS — Sleep · Anxiety · Stress with Just-Imagine copy */}
            <section
                data-testid="sleep-pillars"
                style={{
                    position: "relative",
                    zIndex: 1,
                    marginTop: 64,
                    width: "100%",
                    maxWidth: 980,
                    display: "grid",
                    gridTemplateColumns:
                        "repeat(auto-fit, minmax(260px, 1fr))",
                    gap: 18,
                }}
            >
                {[
                    {
                        kicker: "Pillar 01",
                        title: "Sleep",
                        body: "Just imagine… 8 hours of deep, uninterrupted sleep. Waking up restored — not foggy.",
                        testid: "pillar-sleep",
                    },
                    {
                        kicker: "Pillar 02",
                        title: "Anxiety",
                        body: "Just imagine… Your mind quiet — not racing. The loop in your head — stopped.",
                        testid: "pillar-anxiety",
                    },
                    {
                        kicker: "Pillar 03",
                        title: "Stress",
                        body: "Just imagine… Your body calm — not tense. Stress hormones lowered. The brake — finally working.",
                        testid: "pillar-stress",
                    },
                ].map((p) => (
                    <div
                        key={p.testid}
                        data-testid={p.testid}
                        style={{
                            border: "1px solid rgba(201,162,39,0.22)",
                            padding: "22px 22px 24px",
                            backgroundColor: "rgba(15,15,15,0.6)",
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                        }}
                    >
                        <p
                            style={{
                                color: GOLD,
                                fontSize: 9,
                                letterSpacing: "0.32em",
                                textTransform: "uppercase",
                                margin: 0,
                                fontWeight: 500,
                            }}
                        >
                            {p.kicker}
                        </p>
                        <h3
                            className="font-serif"
                            style={{
                                color: "#fff",
                                fontSize: 28,
                                fontWeight: 400,
                                margin: 0,
                                lineHeight: 1.05,
                                letterSpacing: "-0.015em",
                            }}
                        >
                            {p.title}
                        </h3>
                        <span
                            aria-hidden="true"
                            style={{
                                display: "block",
                                width: 32,
                                height: 1,
                                backgroundColor: "rgba(201,162,39,0.45)",
                            }}
                        />
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 13,
                                lineHeight: 1.65,
                                margin: 0,
                                fontWeight: 300,
                            }}
                        >
                            {p.body}
                        </p>
                    </div>
                ))}
            </section>
        </main>
    );
};

export default Sleep;
